/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useEffect } from 'react';

import { useIntersectionObserver } from '../../hooks/useObserver';
import * as AppPropTypes from '../../lib/PropTypes';

import { useItemRefs } from '../../contexts/ItemRefContext';
import Card from '../cards/Card';
import StickyHeader from '../headers/Sticky';
import Grid from '../partials/Grid';
import ListHeader from '../partials/ListHeader';

import styles from '../../../styles/lists/cards-list.module.scss';

const propTypes = {
    id: PropTypes.string,
    title: PropTypes.node,
    description: PropTypes.node,
    link: PropTypes.oneOfType([AppPropTypes.link, PropTypes.string]),
    cardType: PropTypes.string,
    cardProps: PropTypes.object /* eslint-disable-line */,
    items: AppPropTypes.listItems,
    className: PropTypes.string,
    headerClassName: PropTypes.string,
    headerInnerClassName: PropTypes.string,
    listClassName: PropTypes.string,
    cardClassName: PropTypes.string,
    isDark: PropTypes.bool,
    isGrid: PropTypes.bool,
    gridColumns: PropTypes.number,
    withStickyHeader: PropTypes.bool,
    withMetadata: PropTypes.bool,
    withRef: PropTypes.bool,
    withPlaceholderIndex: PropTypes.bool,
    children: PropTypes.node,
};

const defaultProps = {
    id: null,
    title: null,
    description: null,
    cardType: null,
    cardProps: null,
    link: null,
    items: null,
    className: null,
    headerClassName: null,
    headerInnerClassName: null,
    listClassName: null,
    cardClassName: null,
    isDark: false,
    isGrid: false,
    gridColumns: 0,
    withStickyHeader: false,
    withMetadata: false,
    withRef: false,
    withPlaceholderIndex: false,
    children: null,
};

const CardsList = ({
    id,
    title: listTitle,
    description,
    cardType,
    cardProps,
    link,
    items,
    className,
    headerClassName,
    headerInnerClassName,
    listClassName,
    cardClassName,
    isDark,
    isGrid,
    gridColumns,
    withStickyHeader,
    withMetadata,
    withRef,
    withPlaceholderIndex,
    children,
}) => {
    const { registerRef, unregisterRef, selectRef } = useItemRefs();
    const refItem = useMemo(() => ({ id: id || listTitle, type: 'cardsList' }), [id, listTitle]);

    const cards =
        items !== null &&
        items.map((item, itemIndex) => (
            <Card
                key={`card-${itemIndex + 1}`}
                className={classNames([
                    styles.card,
                    {
                        [cardClassName]: cardClassName !== null,
                    },
                ])}
                item={item}
                type={cardType}
                isDark={isDark}
                withMetadata={withMetadata}
                placeholderIndex={withPlaceholderIndex ? itemIndex : null}
                {...cardProps}
            />
        ));

    const hasCards = cards.length > 0;
    const hasTitle = listTitle !== null;

    const {
        ref,
        entry: { isIntersecting = false },
    } = useIntersectionObserver({ rootMargin: '20% 0% -75% 100%', disabled: !withRef });

    useEffect(() => {
        if (isIntersecting && withRef) {
            selectRef(refItem);
        }
    }, [isIntersecting, refItem, withRef, selectRef]);

    useEffect(() => {
        if (ref && withRef) {
            registerRef(refItem, ref, true);
        }
        return () => unregisterRef(refItem);
    }, [refItem, ref, withRef]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.isDark]: isDark,
                },
            ])}
        >
            {withRef ? <div className={styles.ref} ref={ref} /> : null}
            {hasTitle && !withStickyHeader ? (
                <ListHeader
                    className={classNames([
                        styles.header,
                        {
                            [headerClassName]: headerClassName !== null,
                        },
                    ])}
                    innerClassName={headerInnerClassName}
                    id={id}
                    title={listTitle}
                    description={description}
                    link={link}
                    isDark={isDark}
                />
            ) : null}
            {hasTitle && withStickyHeader ? (
                <StickyHeader
                    className={classNames([
                        styles.header,
                        {
                            [headerClassName]: headerClassName !== null,
                        },
                    ])}
                    innerClassName={headerInnerClassName}
                    id={id}
                    title={listTitle}
                    link={link}
                    isDark={isDark}
                    withRef
                />
            ) : null}
            {children || null}
            {hasCards ? (
                <div
                    className={classNames([
                        styles.list,
                        {
                            [listClassName]: listClassName !== null,
                        },
                    ])}
                >
                    {isGrid ? <Grid columns={gridColumns}>{cards}</Grid> : cards}
                </div>
            ) : null}
        </div>
    );
};

CardsList.propTypes = propTypes;
CardsList.defaultProps = defaultProps;

export default React.forwardRef((props, ref) => <CardsList apiRef={ref} {...props} />);
